import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { WithLauncherProps } from "ehr-launcher-lib";
import { ContextDetails } from "@/types/app-state";
import { buildAthenaContext } from "@/utils/token";
import { getToken } from "@/services/athena";
import { getQueryString } from "@/utils/helpers";
import { TokenResponse } from "@/types/auth";
import { retrieveKeys } from "@/services/access";

const defaultValues = {
  currentUser: "",
  loggedInUser: "",
  authorizationError: "",
  isFetchingContext: false,
  updateAppState: () => null,
};

interface AppState {
  currentUser: string; // dropdown list value
  loggedInUser: string;
  context?: ContextDetails;
  authorizationError: string | undefined;
  isFetchingContext: boolean;
  updateAppState: (partial: Partial<AppState>) => void;
}

const AppStateContext = createContext<AppState>(defaultValues);

export const AppStateContextProvider: FC<
  PropsWithChildren<WithLauncherProps>
> = ({ children }) => {
  const [appState, updateAppState] = useReducer(
    (state: AppState, changes: Partial<AppState>) => ({
      ...state,
      ...changes,
    }),
    {
      ...defaultValues,
    },
  );

  useEffect(() => {
    const launchId = getQueryString("launch");
    const authCode = getQueryString("code");
    const abortController = new AbortController();

    if (process.env.REACT_APP_APP_ENV === "local") {
      updateAppState({
        isFetchingContext: true,
      });
      getToken({ code: "local" })
        .then((data: TokenResponse) => {
          const context = buildAthenaContext(data.token);
          sessionStorage.setItem("token", data.token.access_token);
          updateAppState({
            currentUser: "All",
            context,
            loggedInUser: context.user,
            isFetchingContext: false,
          });
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }

    if (!authCode && launchId) {
      updateAppState({
        isFetchingContext: true,
      });
      retrieveKeys(
        "ATHENA_CLIENT_ID,ATHENA_AUD,ATHENA_AUTHORIZATION_URI,ATHENA_REDIRECT_URI",
        abortController,
      )
        .then((keys) => {
          window.location.href = `${keys.ATHENA_AUTHORIZATION_URI}?client_id=${keys.ATHENA_CLIENT_ID}&state=asd12344446&scope=launch%20offline_access&redirect_uri=${keys.ATHENA_REDIRECT_URI}&response_type=code&launch=${launchId}&aud=${keys.ATHENA_AUD}`;
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }
    if (authCode) {
      updateAppState({
        isFetchingContext: true,
      });
      getToken({ code: authCode })
        .then((data: TokenResponse) => {
          const context = buildAthenaContext(data.token);
          sessionStorage.setItem("token", data.token.access_token);
          updateAppState({
            currentUser: "All",
            context,
            loggedInUser: context.user,
            isFetchingContext: false,
          });
        })
        .catch((error) => {
          console.error(error);
          updateAppState({
            isFetchingContext: false,
            authorizationError: "Failed to authorize user.",
          });
        });
    }

    return () => abortController.abort();
  }, []);

  return (
    <AppStateContext.Provider value={{ ...appState, updateAppState }}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => useContext(AppStateContext);
